import { Button, Input, List, Modal, Space, Tag, Typography } from "@arco-design/web-react";
import { RefInputType } from '@arco-design/web-react/lib/Input';
import { User } from "@sendbird/chat/lib/__definition";

import { useSendbirdStateContext } from "@sendbird/uikit-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import { getButtonColor, getButtonTextColor } from "../../../../utils/cssVariables";
import { searchPlatformUsers } from "../../utils/platformUserSearch";

import "./StartGroupChatDialog.css";
import { MemberWithMetaData } from '../../types';

type Props = {
  visible: boolean;
  onClose: () => void;
  onMessagingStart: (name: string, users: User[]) => Promise<void>;
};

export function StartGroupChatDialog({
                                       visible,
                                       onClose,
                                       onMessagingStart,
                                     }: Props) {
  const { t } = useTranslation();
  const userInputRef = useRef<RefInputType>(null);
  const currentUserID = useSelector(
    (state: RootState) => state.user.user?.id
  );
  const [usersList, setUsersList] = useState<User[]>([]);
  const [showedUsersList, setShowedUsersList] = useState<User[]>([]);
  const [selectedUsersList, setSelectedUsersList] = useState<User[]>([]);
  const [blockedUserIds, setBlockedUserIds] = useState<string[]>([]);
  const [filter, setFilter] = useState<string>("");
  const [groupName, setGroupName] = useState<string>("");
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const {
    stores: {
      sdkStore: { sdk },
    },
  } = useSendbirdStateContext();
  const bUsersQuery = sdk.createBlockedUserListQuery?.();

  const getShowedUsers = (list: User[]) => {
    return list
      .filter((user) => {
        const isCurrentUser = user.userId === currentUserID?.toString();
        const isSelectedUser = selectedUsersList.some(selectedUser => selectedUser.userId === user.userId);
        return !isCurrentUser && !isSelectedUser;
      })
      .slice(0, 5)
  }

  useEffect(() => {
    const fetchData = async () => {
      const usersData = await searchPlatformUsers(filter);

      if (usersData.length) {
        setUsersList(usersData);
        setShowedUsersList(getShowedUsers(usersData));
      } else {
        setUsersList([]);
        setShowedUsersList([]);
      }
    };

    if (visible && filter) {
      fetchData().catch(console.error);
    } else {
      setUsersList([]);
      setShowedUsersList([]);
    }
  }, [filter, visible]);

  useEffect(() => {
    const fetchData = async () => {
      const blockedUsersData: User[] = await bUsersQuery?.next();

      if (blockedUsersData && blockedUsersData.length) {
        setBlockedUserIds(blockedUsersData.map((user) => user.userId));
      }
    };

    if (visible) {
      fetchData().catch(console.error);
    }
  }, [visible]);

  useEffect(() => {
    setShowedUsersList(getShowedUsers(usersList));
  }, [selectedUsersList.length]);

  const handleSelectUser = (user: User) => {
    setSelectedUsersList(prevState => [...prevState, user]);
    userInputRef.current?.focus();
    setFilter('');
  };

  const handleRemoveUser = (user: User) => {
    setSelectedUsersList(prevState => prevState.filter(selectedUser => selectedUser.userId !== user.userId));
  };

  const handleUnblockAndSelect = (user: User) => {
    sdk.unblockUserWithUserId(user.userId).then(() => {
      handleSelectUser(user);
    });
  };

  const handleCreateGroupChat = async () => {
    if (!groupName.trim() || selectedUsersList.length === 0) {
      return;
    }

    setIsCreating(true);
    setCreateError(null);
    try {
      await onMessagingStart(groupName.trim(), selectedUsersList);
      onClose();
      resetForm();
    } catch {
      setCreateError(t("messages.create_group_chat_error"));
    } finally {
      setIsCreating(false);
    }
  };

  const resetForm = () => {
    setFilter('');
    setGroupName('');
    setSelectedUsersList([]);
    setShowedUsersList([]);
  }

  const renderUser = (item: MemberWithMetaData) => {
    const isBlocked = blockedUserIds.includes(item.userId);

    return (
      <List.Item
        key={item.userId}
        style={{ cursor: "pointer" }}
        onClick={() =>
          isBlocked ? handleUnblockAndSelect(item) : handleSelectUser(item)
        }
        actions={[
          <Button
            style={{
              color: getButtonTextColor(),
              backgroundColor: getButtonColor(),
            }}
            size={"mini"}
            shape={"round"}
            onClick={(event) => {
              event.stopPropagation();
              if (isBlocked) {
                handleUnblockAndSelect(item);
              } else {
                handleSelectUser(item);
              }
            }}
          >
            {isBlocked
              ? t("messages.unblock_and_start_messaging")
              : t("messages.add_member_select_button")}
          </Button>,
        ]}
      >
        <List.Item.Meta
          title={
            <div>
              {item.nickname}
              {isBlocked && (
                <span className="start-conversation-dialog__blocked-user">
                  {t('messages.is_blocked')}
                </span>
              )}
            </div>
          }
        />
      </List.Item>
    );
  };

  return (
    <Modal
      title={t('messages.group_chat_modal_title')}
      visible={visible}
      onCancel={onClose}
      autoFocus
      focusLock={true}
      className={"start-group-chat-dialog"}
      footer={null}
    >
      <div className="start-group-chat-dialog__content">
        <Input
          allowClear
          value={groupName}
          onChange={setGroupName}
          placeholder={t('messages.group_name_placeholder')}
        />
        {
          selectedUsersList.length > 0 ?
            (
              <Space size="mini" wrap>
                {selectedUsersList
                  .map((user: User) => (<Tag key={user.userId} closable onClose={() => handleRemoveUser(user)}>{user.nickname}</Tag>))}
              </Space>
            )
            : (
              <div className="start-group-chat-dialog__selected-users-placeholder">
                <Typography.Text type="secondary">
                  {t("messages.users_list_placeholder")}
                </Typography.Text>
              </div>
            )
        }

        {createError && (
          <Typography.Text type="error">{createError}</Typography.Text>
        )}

        <Input
          allowClear
          value={filter}
          onChange={setFilter}
          placeholder={t('messages.user_search_placeholder')}
          ref={userInputRef}
        />
        <List
          className="list-demo-actions"
          size="small"
          dataSource={showedUsersList}
          render={renderUser}
          noDataElement={
            <div className="start-group-chat-dialog__no-users-message">
              {t('messages.find_users_placeholder')}
            </div>
          }
        />
        <Button
          className="start-group-chat-dialog__button"
          shape="round"
          loading={isCreating}
          onClick={handleCreateGroupChat}
          disabled={!groupName.trim() || selectedUsersList.length === 0 || isCreating}
        >
          {t('messages.create_group_chat_button')}
        </Button>
      </div>
    </Modal>
  );
}
